import { useAccountContext } from '@/context/account'
import type { CctpTransactionsResponse } from '@/queries/cctpTransactions.query'
import { invalidateQueriesGlobally } from '@/utils/queries/invalidateQueriesGlobally'
import { waitForTransactionSuccess } from '@/utils/transactions/waitForTransactionSuccess'
import type { Network } from '@vaultsfyi/common/types'
import { getChainId } from '@vaultsfyi/common/utils'
import { useCallback, useState } from 'react'
import { type Address, type Hex, UserRejectedRequestError } from 'viem'
import { useConfig } from 'wagmi'
import { sendTransaction, switchChain } from 'wagmi/actions'

export const CCTP_NETWORKS: Network[] = ['mainnet', 'arbitrum', 'base']

export interface BridgeStep {
  name: string
  state: 'pending' | 'success' | 'error'
  txHash?: string
}

type BridgeResult =
  | { status: 'success'; txHash: string }
  | { status: 'rejected' }
  | { status: 'error'; message: string }

export type ClaimResult = { status: 'success' } | { status: 'rejected' } | { status: 'error'; message: string }

function isUserRejection(error: unknown): boolean {
  return (
    error instanceof UserRejectedRequestError ||
    (error as { shortMessage?: string })?.shortMessage === 'User rejected the request.'
  )
}

export function useCctpBridge() {
  const wagmiConfig = useConfig()
  const { chainId: connectedChainId } = useAccountContext()
  const [isBridging, setIsBridging] = useState(false)
  const [isClaiming, setIsClaiming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [bridgeSteps, setBridgeSteps] = useState<BridgeStep[]>([])
  const [claimSteps, setClaimSteps] = useState<BridgeStep[]>([])

  const executeBridge = useCallback(
    async (actionsData: CctpTransactionsResponse, srcNetwork: Network): Promise<BridgeResult> => {
      setIsBridging(true)
      setError(null)
      setBridgeSteps(actionsData.actions.map((a) => ({ name: a.name, state: 'pending' })))
      try {
        const srcChainId = getChainId(srcNetwork)
        if (connectedChainId !== srcChainId) {
          await switchChain(wagmiConfig, { chainId: srcChainId })
        }

        let lastTxHash: string | undefined
        for (let i = actionsData.currentActionIndex; i < actionsData.actions.length; i++) {
          const { tx } = actionsData.actions[i]
          const hash = await sendTransaction(wagmiConfig, {
            to: tx.to as Address,
            data: tx.data as Hex,
            chainId: tx.chainId,
          })
          await waitForTransactionSuccess(wagmiConfig, { hash })
          setBridgeSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, state: 'success', txHash: hash } : s)))
          lastTxHash = hash
        }

        if (!lastTxHash) throw new Error('No transaction hash received')

        await invalidateQueriesGlobally()
        return { status: 'success', txHash: lastTxHash }
      } catch (err) {
        if (isUserRejection(err)) {
          setBridgeSteps([])
          return { status: 'rejected' }
        }
        const message = err instanceof Error ? err.message : 'Bridge failed'
        setError(message)
        setBridgeSteps((prev) => {
          const pendingIdx = prev.findIndex((s) => s.state === 'pending')
          if (pendingIdx >= 0) return prev.map((s, i) => (i === pendingIdx ? { ...s, state: 'error' } : s))
          return prev
        })
        return { status: 'error', message }
      } finally {
        setIsBridging(false)
      }
    },
    [wagmiConfig, connectedChainId]
  )

  const executeClaim = useCallback(
    async (actionsData: CctpTransactionsResponse, dstNetwork: Network): Promise<ClaimResult> => {
      setIsClaiming(true)
      setError(null)
      setClaimSteps(actionsData.actions.map((a) => ({ name: a.name, state: 'pending' })))
      try {
        const dstChainId = getChainId(dstNetwork)
        if (connectedChainId !== dstChainId) {
          await switchChain(wagmiConfig, { chainId: dstChainId })
        }

        for (let i = actionsData.currentActionIndex; i < actionsData.actions.length; i++) {
          const { tx } = actionsData.actions[i]
          const hash = await sendTransaction(wagmiConfig, {
            to: tx.to as Address,
            data: tx.data as Hex,
            chainId: tx.chainId,
          })
          await waitForTransactionSuccess(wagmiConfig, { hash })
          setClaimSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, state: 'success', txHash: hash } : s)))
        }

        await invalidateQueriesGlobally()
        return { status: 'success' }
      } catch (err) {
        if (isUserRejection(err)) {
          setClaimSteps([])
          return { status: 'rejected' }
        }
        const message = err instanceof Error ? err.message : 'Claim failed'
        setError(message)
        setClaimSteps((prev) => {
          const pendingIdx = prev.findIndex((s) => s.state === 'pending')
          if (pendingIdx >= 0) return prev.map((s, i) => (i === pendingIdx ? { ...s, state: 'error' } : s))
          return prev
        })
        return { status: 'error', message }
      } finally {
        setIsClaiming(false)
      }
    },
    [wagmiConfig, connectedChainId]
  )

  return {
    isBridging,
    isClaiming,
    error,
    clearError: () => setError(null),
    bridgeSteps,
    claimSteps,
    clearSteps: () => {
      setBridgeSteps([])
      setClaimSteps([])
    },
    executeBridge,
    executeClaim,
  }
}
